import React from "react";
import type { TrainState } from "../../../../models";
import { removeTrainCar } from "../../../../models";
import { isTrainCarId, TRAIN_ID_BASE } from "../train/trainReconcile";
import { confineToOneLeaf } from "../train/trainSelect";

interface MemberIndex {
  code: Map<number, string>;
  leaf: Map<number, string>;
}

interface Args {
  memberIndex: MemberIndex;
  protoTrain: TrainState | null;
  setProtoTrain: React.Dispatch<React.SetStateAction<TrainState | null>>;
  removeProtoNewMany: (activityIds: number[]) => void;
  requestDelete: (graphIds: string[]) => Promise<void>;
  deleteTrainMappedRefs: (refs: string[]) => void;
  pushUndo: (label?: string) => void;
}

export interface SelectionState {
  protoSelection: Set<number>;
  setProtoSelection: React.Dispatch<React.SetStateAction<Set<number>>>;
  handleSelectionChange: (next: Set<number>) => void;
  protoBulkDelete: () => void;
}

export function useSelection({
  memberIndex,
  protoTrain,
  setProtoTrain,
  removeProtoNewMany,
  requestDelete,
  deleteTrainMappedRefs,
  pushUndo,
}: Args): SelectionState {
  // Moved/resized bars: committed members get a span override (visual; dates
  // ride the forward pass at publish), draft-new bars patch their entry.
  const [protoSelection, setProtoSelection] = React.useState<Set<number>>(
    () => new Set(),
  );

  const protoBulkDelete = React.useCallback(() => {
    const all = Array.from(protoSelection);
    const trainIds = all.filter(isTrainCarId);
    const ids = all.filter((id) => !isTrainCarId(id));
    const newIds = ids.filter((id) => id < 0);
    const committed = ids.filter((id) => id >= 0).map(String);
    if (trainIds.length > 0 && protoTrain) {
      // Resolve carIds against the CURRENT train up front — removeTrainCar
      // reindexes the array, so TRAIN_ID_BASE-i lookups would drift mid-loop.
      const carIds = trainIds
        .map((id) => protoTrain.cars[TRAIN_ID_BASE - id]?.carId)
        .filter((c): c is string => !!c);
      pushUndo(
        trainIds.length > 1
          ? `Remove ${trainIds.length} train elements`
          : "Remove train element",
      );
      let next = protoTrain;
      for (const carId of carIds) next = removeTrainCar(next, carId);
      if (next.cars.length === 0) {
        deleteTrainMappedRefs(next.pendingRowDeletes ?? []);
        setProtoTrain(null);
      } else {
        setProtoTrain(next); // row removals deferred to Apply
      }
    }
    if (newIds.length > 0) removeProtoNewMany(newIds);
    if (committed.length > 0) void requestDelete(committed);
    setProtoSelection(new Set());
  }, [protoSelection, removeProtoNewMany, requestDelete, pushUndo, protoTrain, setProtoTrain, deleteTrainMappedRefs]);

  // A selection is built from ONE level: a click that lands on a different leaf
  // restarts the selection there, so a run never spans levels.
  const handleSelectionChange = React.useCallback(
    (next: Set<number>) =>
      setProtoSelection(
        confineToOneLeaf(next, protoSelection, (id) => memberIndex.leaf.get(id) ?? null),
      ),
    [protoSelection, memberIndex],
  );

  return { protoSelection, setProtoSelection, handleSelectionChange, protoBulkDelete };
}
